import type { RegionManifest, Species } from '../types';

// The species universe the whole app studies against.
//
// Region manifests live as static assets under /data/regions/<id>/region.json so
// they are precached with the app shell and swappable when regions are added.
// One pack is the normal case; the learner can also pick "All packs", which is
// why this holds a list rather than a single manifest. Everything downstream
// reads the union, so the two cases are one code path with a list of one or a
// list of many — there is no separate all-packs mode to keep in step.
const DEFAULT_REGION = 'pnw';

/** The value `packs.active` takes when the learner wants every pack at once. */
export const ALL_PACKS = 'all';

let manifests: RegionManifest[] = [];
const byCode = new Map<string, Species>();
const packByCode = new Map<string, string>();
const positionByCode = new Map<string, number>();

/** Every pack the app ships, as listed by /data/regions/index.json. */
export interface PackSummary {
  id: string;
  label: string;
  version: string;
  speciesCount: number;
  packFiles: number;
  packBytes: number;
}

/** Distinct species across every pack — what "All packs" teaches. */
let unionCount = 0;
export function unionSpeciesCount(): number {
  return unionCount;
}

let packIndex: PackSummary[] | null = null;

/**
 * The packs available to choose between.
 *
 * A separate, tiny file rather than reading every manifest: the pack picker has
 * to name and size each option, and region.json is ~3 KB per species — 445 KB
 * for the PNW pack alone. Precached with the shell like the manifests are, so
 * the picker works offline.
 *
 * Falls back to a single synthetic entry for the default region if the index is
 * missing, which is what a checkout with a hand-built manifest and no pipeline
 * run looks like. A missing index should cost the picker, not the app.
 */
export async function loadPackIndex(): Promise<PackSummary[]> {
  if (packIndex) return packIndex;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}data/regions/index.json`);
    if (!res.ok) throw new Error(String(res.status));
    const data = (await res.json()) as {
      regions?: PackSummary[];
      unionSpeciesCount?: number;
    };
    if (!data.regions?.length) throw new Error('empty index');
    packIndex = data.regions;
    // Ranges overlap, so the union is smaller than the sum — see the pipeline's
    // writePackIndex. Fall back to the sum for an index written before this
    // field existed: an overcount by the handful of shared birds is a wrong
    // label, where a zero would be a broken one.
    unionCount =
      data.unionSpeciesCount ?? data.regions.reduce((n, p) => n + p.speciesCount, 0);
  } catch {
    packIndex = [
      {
        id: DEFAULT_REGION,
        label: DEFAULT_REGION,
        version: '',
        speciesCount: 0,
        packFiles: 0,
        packBytes: 0,
      },
    ];
  }
  return packIndex;
}

/**
 * Turn a stored `packs.active` value into the list of region ids to load.
 *
 * Tolerant on purpose. The setting is synced across devices and outlives any
 * particular release, so it can name a pack this build no longer ships — a
 * learner who studied a pack that was later withdrawn should get the default
 * back, not the fatal "Could not load region data" screen.
 */
export function resolveSelection(selection: string | undefined, available: PackSummary[]): string[] {
  const ids = available.map((p) => p.id);
  if (selection === ALL_PACKS && ids.length) return ids;
  if (selection && ids.includes(selection)) return [selection];
  return [ids.includes(DEFAULT_REGION) ? DEFAULT_REGION : (ids[0] ?? DEFAULT_REGION)];
}

export async function loadRegions(ids: string[] = [DEFAULT_REGION]): Promise<RegionManifest[]> {
  const loaded = await Promise.all(ids.map(fetchManifest));
  setRegions(loaded);
  return loaded;
}

/**
 * One pack's manifest, fetched without installing it.
 *
 * Two screens need to read a pack they are not studying — the You screen sizes
 * every pack's download, and a species page has to say which pack a bird it
 * cannot find belongs to. Both want the file, neither wants `setRegions`, so
 * this is deliberately separate from `loadRegions`.
 */
export function fetchPackManifest(id: string): Promise<RegionManifest> {
  return fetchManifest(id);
}

/**
 * Which shipped pack carries a species, selected or not.
 *
 * Answers the dead end a deep link now has: with packs selectable, a link to a
 * bird is a link to a bird in *some* pack, and "unknown species" is the wrong
 * answer when the right one is "switch packs".
 */
export async function findPackFor(code: string): Promise<PackSummary | null> {
  const available = await loadPackIndex();
  for (const summary of available) {
    try {
      const m = await fetchPackManifest(summary.id);
      if (m.species.some((sp) => sp.code === code)) return summary;
    } catch {
      // A pack whose manifest won't load can't be recommended; try the next.
    }
  }
  return null;
}

async function fetchManifest(id: string): Promise<RegionManifest> {
  const res = await fetch(`${import.meta.env.BASE_URL}data/regions/${id}/region.json`);
  if (!res.ok) {
    throw new Error(`Failed to load region "${id}": ${res.status}`);
  }
  return (await res.json()) as RegionManifest;
}

/** Back-compat shim: one region is just a list of one. */
export async function loadRegion(id: string = DEFAULT_REGION): Promise<RegionManifest> {
  const [manifest] = await loadRegions([id]);
  return manifest;
}

/**
 * Install manifests as the active set. `loadRegions` uses this after fetching;
 * tests use it to run the quiz builders against the real manifest read off disk.
 *
 * Deduped by species code, earlier manifest winning. eBird codes are global, so
 * a bird in two packs — House Sparrow, European Starling, Rock Pigeon — is the
 * same bird and the same FSRS card either way. Carrying it twice in the union
 * would make it twice as likely to be asked as any other bird, purely for having
 * a wide range, so the first pack that claims a code owns its media, its
 * confusables and its taxonomic position, and the union counts it once.
 */
export function setRegions(list: RegionManifest[]): void {
  manifests = list;
  byCode.clear();
  packByCode.clear();
  positionByCode.clear();
  for (const manifest of list) {
    manifest.species.forEach((sp, i) => {
      if (byCode.has(sp.code)) return;
      byCode.set(sp.code, sp);
      packByCode.set(sp.code, manifest.id);
      positionByCode.set(sp.code, i);
    });
  }
}

/** Back-compat shim, and what every quiz test still calls. */
export function setRegion(manifest: RegionManifest): void {
  setRegions([manifest]);
}

/**
 * The primary manifest — the first active pack.
 *
 * Only for the things that genuinely want one pack's own metadata: its label,
 * its version, its pack base URL. Anything asking about the species universe
 * wants `speciesList`/`speciesCodes`, which span the whole active set.
 */
export function region(): RegionManifest {
  if (!manifests.length) throw new Error('Region not loaded — call loadRegions() first.');
  return manifests[0];
}

/** Every active pack, in selection order. */
export function activePacks(): RegionManifest[] {
  return manifests;
}

export function speciesList(): Species[] {
  return [...byCode.values()];
}

export function speciesCodes(): string[] {
  return [...byCode.keys()];
}

export function getSpecies(code: string): Species | undefined {
  return byCode.get(code);
}

/** Which pack a species is being taught from — see the dedupe rule above. */
export function packIdOf(code: string): string | undefined {
  return packByCode.get(code);
}

/**
 * Narrow a pool to the pack the target species belongs to.
 *
 * Distractors have to come from one pack. A Cape Sugarbird offered against a
 * Song Sparrow is not a distractor a learner could fall for — the continent
 * gives it away, so the question is answerable without knowing the bird, which
 * is the free mark the graded cascade exists to remove. It also keeps
 * `taxonomicPosition` meaningful: positions are indices into one manifest, so a
 * distance taken across two of them measures nothing.
 *
 * A no-op when a single pack is selected, which is the common case.
 */
export function packPool(code: string, pool: string[]): string[] {
  if (manifests.length < 2) return pool;
  const pack = packByCode.get(code);
  if (!pack) return pool;
  return pool.filter((c) => packByCode.get(c) === pack);
}

/**
 * The day each pack last gained species, keyed by pack id.
 *
 * Per pack rather than one date across the selection, and that is the whole
 * subtlety here: a single `max(addedAt)` would report only whichever pack was
 * added to most recently, so the evening the Pacific Northwest gained twenty
 * birds, Cape Town's own newest twenty would silently stop being new.
 *
 * Packs with nothing dated are absent rather than present-and-empty — the
 * species added before the pipeline stamped the day carry no date and never
 * will, and neither does a `region.json` precached before the field existed.
 */
export function newestBatchDays(from: Species[]): Map<string, string> {
  const days = new Map<string, string>();
  for (const sp of from) {
    if (!sp.addedAt) continue;
    const pack = packByCode.get(sp.code) ?? '';
    const seen = days.get(pack);
    if (!seen || sp.addedAt > seen) days.set(pack, sp.addedAt);
  }
  return days;
}

/**
 * The species added on each pack's most recent day, in the order they were
 * given — the pack's newest batch.
 *
 * The most recent DAY, not a rolling window, which is the same rule the
 * curation tool applies to the same field and for the same reason: a batch is
 * worked through over several sittings, and a window that expired halfway
 * would leave a learner mid-way through twenty birds with no way to ask for the
 * rest of them.
 *
 * It exists because the newest birds are otherwise the hardest to reach.
 * Batches are added commonest-first, so a new twenty lands at the back of the
 * region by frequency rank — and `queueTiers` introduces unmet species in rank
 * order, a fraction of a round at a time. The newest batch is therefore the
 * last thing the schedule would ever get to, which is the opposite of what
 * someone who just downloaded it wants.
 *
 * Pass `studyList()` rather than `speciesList()` to leave out the birds that
 * have been set aside, so a batch of twenty with three of them put away is a
 * round of seventeen.
 */
export function newestBatch(from: Species[]): Species[] {
  const days = newestBatchDays(from);
  if (!days.size) return [];
  return from.filter((sp) => sp.addedAt && sp.addedAt === days.get(packByCode.get(sp.code) ?? ''));
}

/**
 * Whether a species had joined its pack by `day`, a `yyyy-mm-dd` key.
 *
 * A species with no date is one of the originals and was always there. Birdle
 * asks this of every day it draws, so that a bird added in October cannot turn
 * out to have been September's answer.
 */
export function inPackBy(sp: Species, day: string): boolean {
  return !sp.addedAt || sp.addedAt <= day;
}

/**
 * A species' position in its own pack's ordering, which is taxonomic — stage 06
 * sorts `region.json` by checklist index. So the distance between two positions
 * is a usable measure of how closely related two birds are, finer than family
 * and much finer than order, without the manifest having to carry a taxonomic
 * key of its own.
 *
 * Positions are dense over the species actually in the app, which is the right
 * scale: "the nearest twelve birds you could be asked about" rather than the
 * nearest twelve in a checklist mostly absent from the region. They are dense
 * *per pack*, and only ever compared within one — see `packPool`.
 */
export function taxonomicPosition(code: string): number | undefined {
  return positionByCode.get(code);
}
